/**
 * Conversões sRGB ↔ OKLCH e derivação da escala de marca.
 *
 * O personal escolhe a cor; o SISTEMA garante a legibilidade. Sem dependência
 * externa: são ~120 linhas e cada garantia é verificada por teste.
 * Ver docs/specs/03-design-system.md.
 */

export interface Rgb {
  r: number
  g: number
  b: number
}

export interface Oklch {
  l: number
  c: number
  h: number
}

// ── Parsing ───────────────────────────────────────────────────

export function parseHex(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '')

  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null

  return {
    r: parseInt(full.slice(0, 2), 16) / 255,
    g: parseInt(full.slice(2, 4), 16) / 255,
    b: parseInt(full.slice(4, 6), 16) / 255,
  }
}

export function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number): string =>
    Math.round(Math.min(1, Math.max(0, value)) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase()
}

// ── sRGB ↔ OKLab ↔ OKLCH ──────────────────────────────────────

const linearize = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4

const delinearize = (c: number): number =>
  c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055

export function rgbToOklch({ r, g, b }: Rgb): Oklch {
  const lr = linearize(r)
  const lg = linearize(g)
  const lb = linearize(b)

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  const chroma = Math.sqrt(okA * okA + okB * okB)
  const hue = chroma < 1e-6 ? 0 : ((Math.atan2(okB, okA) * 180) / Math.PI + 360) % 360

  return { l: okL, c: chroma, h: hue }
}

export function oklchToRgb({ l, c, h }: Oklch): Rgb {
  const hRad = (h * Math.PI) / 180
  const a = c * Math.cos(hRad)
  const bb = c * Math.sin(hRad)

  const lRoot = l + 0.3963377774 * a + 0.2158037573 * bb
  const mRoot = l - 0.1055613458 * a - 0.0638541728 * bb
  const sRoot = l - 0.0894841775 * a - 1.291485548 * bb

  const lc = lRoot ** 3
  const mc = mRoot ** 3
  const sc = sRoot ** 3

  return {
    r: delinearize(4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc),
    g: delinearize(-1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc),
    b: delinearize(-0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc),
  }
}

/** Reduz o croma até a cor caber no gamut sRGB, preservando L e matiz. */
export function clampToGamut(color: Oklch): Oklch {
  let { c } = color
  for (let i = 0; i < 24; i += 1) {
    const rgb = oklchToRgb({ ...color, c })
    const inGamut =
      rgb.r >= -0.001 && rgb.r <= 1.001 && rgb.g >= -0.001 && rgb.g <= 1.001 && rgb.b >= -0.001 && rgb.b <= 1.001
    if (inGamut) break
    c *= 0.9
  }
  return { ...color, c }
}

export function oklchToHex(color: Oklch): string {
  return toHex(oklchToRgb(clampToGamut(color)))
}

// ── Contraste (WCAG 2.1) ──────────────────────────────────────

export function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

export function contrastHex(a: string, b: string): number {
  const rgbA = parseHex(a)
  const rgbB = parseHex(b)
  if (!rgbA || !rgbB) return 1
  return contrastRatio(rgbA, rgbB)
}

// ── Derivação da escala de marca ──────────────────────────────

export interface BrandScale {
  brand: string
  brandFg: string
  brandText: string
  brandHover: string
  brandActive: string
  brandSubtle: string
  brandSubtleFg: string
  brandRing: string
}

const WHITE = '#FFFFFF'
const NEAR_BLACK = '#0C1116'

/**
 * Ajusta a luminosidade até atingir a razão de contraste desejada contra
 * `background`, preservando matiz e croma o quanto possível. É isto que
 * impede que uma escolha como #FFE600 produza um valor ilegível.
 */
function ensureContrast(
  color: Oklch,
  background: Rgb,
  target: number,
  direction: 'darker' | 'lighter',
): string {
  const step = direction === 'darker' ? -0.01 : 0.01
  const current = { ...color }
  let fallback = toHex(oklchToRgb(clampToGamut(current)))

  for (let i = 0; i < 100; i += 1) {
    const hex = toHex(oklchToRgb(clampToGamut(current)))

    // O contraste é medido no valor JÁ QUANTIZADO para 8 bits por canal.
    // Medir no float deixa passar casos que falham por centésimos depois
    // de virarem hex — foi exatamente assim que #FCBABE escapou com 4.483.
    if (contrastRatio(parseHex(hex) as Rgb, background) >= target) return hex

    fallback = hex
    current.l += step

    if (current.l <= 0.02 || current.l >= 0.995) break
  }

  // Nenhuma luminosidade atingiu o alvo mantendo o matiz: vai para o
  // extremo legível, porque legibilidade vence fidelidade de marca.
  const extreme = direction === 'darker' ? NEAR_BLACK : WHITE
  return contrastRatio(parseHex(extreme) as Rgb, background) >= target ? extreme : fallback
}

export function deriveBrandScale(primary: string, mode: 'light' | 'dark'): BrandScale {
  const rgb = parseHex(primary) ?? (parseHex('#2B59FF') as Rgb)
  const base = rgbToOklch(rgb)
  const isDark = mode === 'dark'

  const pageBg = parseHex(isDark ? '#0C1116' : '#FBFCFD') as Rgb

  // Fundo do botão: a cor escolhida, tal qual.
  const brand = toHex(oklchToRgb(clampToGamut(base)))

  // Texto SOBRE a marca: branco ou quase-preto, o que contrastar mais.
  const whiteRgb = parseHex(WHITE) as Rgb
  const blackRgb = parseHex(NEAR_BLACK) as Rgb
  const brandRgb = parseHex(brand) as Rgb
  const brandFg =
    contrastRatio(whiteRgb, brandRgb) >= contrastRatio(blackRgb, brandRgb) ? WHITE : NEAR_BLACK

  // Marca usada COMO TEXTO sobre o fundo da página: precisa de 4.5:1.
  const brandText = ensureContrast(base, pageBg, 4.5, isDark ? 'lighter' : 'darker')

  // Anel de foco: elemento de interface, 3:1 basta.
  const brandRing = ensureContrast(base, pageBg, 3, isDark ? 'lighter' : 'darker')

  const brandHover = oklchToHex({ ...base, l: clamp01(base.l + (isDark ? 0.05 : -0.04)) })
  const brandActive = oklchToHex({ ...base, l: clamp01(base.l + (isDark ? 0.09 : -0.08)) })

  // Fundo de badge: mesma família, quase sem croma.
  const subtle: Oklch = { l: isDark ? 0.22 : 0.955, c: base.c * 0.18, h: base.h }
  const brandSubtle = oklchToHex(subtle)
  const subtleRgb = parseHex(brandSubtle) as Rgb
  const brandSubtleFg = ensureContrast(base, subtleRgb, 4.5, isDark ? 'lighter' : 'darker')

  return {
    brand,
    brandFg,
    brandText,
    brandHover,
    brandActive,
    brandSubtle,
    brandSubtleFg,
    brandRing,
  }
}

function clamp01(value: number): number {
  return Math.min(0.99, Math.max(0.02, value))
}

/**
 * O personal informa fundo e texto como preferência. Se o par não atingir
 * 4.5:1, mantemos a intenção de matiz e ajustamos a luminosidade.
 */
export function ensureReadablePair(
  background: string,
  text: string,
): { background: string; text: string; adjusted: boolean } {
  const bgRgb = parseHex(background)
  const textRgb = parseHex(text)
  if (!bgRgb || !textRgb) return { background, text, adjusted: false }

  if (contrastRatio(bgRgb, textRgb) >= 4.5) {
    return { background, text, adjusted: false }
  }

  const bgIsLight = relativeLuminance(bgRgb) > 0.4
  const fixed = ensureContrast(rgbToOklch(textRgb), bgRgb, 4.5, bgIsLight ? 'darker' : 'lighter')

  return { background, text: fixed, adjusted: true }
}
